// The filing, done and gone screens: what a bound session says while its photos are filed, and the Saved screen with its return to Shopify.
import { itemName, itemPictures } from "../../lib/identityDecision";
import { doneReturnUrl } from "../../lib/returnUrl";
import { doneScreen } from "../../lib/doneScreen";
import { isIOS } from "../../lib/deviceInfo";
import { releaseShot } from "./shotCapture";

const LAPTOP = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="5" width="16" height="11" rx="1.5" /><path d="M2 19h20" /></svg>
);
const TICK = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
);

/** Bulk finished from the desktop QR: nothing to tap, the desktop has the table. */
export function DeskEnd({ n }) {
  return (
    <div className="cap-saved" role="status">
      <div className="cap-saved-pic" />
      <span className="cap-saved-pill cap-saved-pill--desk">
        {TICK}
        {`Saved · ${n} item${n === 1 ? "" : "s"}`}
      </span>
      <span className="cap-saved-name">Bulk scan finished</span>
      <span className="cap-saved-line">
        {LAPTOP}
        Opened on your computer
      </span>
    </div>
  );
}

export function SavedScreen({ autoReturn, awaitingRef, barcode, doneSnap, dropTraining, ident, isPreset, onBack, onFileCand, phase, preset, presetStuck, presetTitle, queueRef, quick, savedUi, setAwaiting, setPage, setShots, shots, shotsRef, takeMore }) {
  return (
    <>
      {/* PRESET: waiting on the commit, or saying why it did not land. */}
{phase === "filing" ? (
        <div className="cap-panel" aria-busy={!presetStuck} role={presetStuck ? "alert" : "status"}>
          {presetStuck === "gone" ? (
            <>
              <h2>This product is gone</h2>
              <p>
                It was deleted in Shopify, so these photos could not be added
                to it. Go back to Shopify.
              </p>
              <button
                type="button"
                className="cap-btn cap-btn--ghost"
                onClick={() => {
                  // Explicit and confirmed, as the queue's contract asks.
                  if (!window.confirm("Discard these photos? They cannot be filed.")) return;
                  queueRef.current.discardFailed();
                  awaitingRef.current = null;
                  setAwaiting(null);
                  shotsRef.current.forEach(releaseShot);
                  dropTraining();
                  setShots([]);
                  setPage("gone");
                }}
              >
                Discard photos
              </button>
            </>
          ) : presetStuck === "signed_out" ? (
            <>
              <h2>Signed out</h2>
              <p>
                Your photos are still on this device. Ask your admin for a new
                link.
              </p>
            </>
          ) : presetStuck === "failed" ? (
            <>
              <h2>Photos not added yet</h2>
              <p>
                {shots.length === 1 ? "Your photo is" : `Your ${shots.length} photos are`}{" "}
                safe on this device. Check the signal and try again.
              </p>
              <button type="button" className="cap-btn" onClick={() => queueRef.current.retry()}>
                Try again
              </button>
            </>
          ) : (
            <>
              <h2>Adding photos…</h2>
              <p>
                Filing {shots.length === 1 ? "1 photo" : `${shots.length} photos`} to{" "}
                <b>{presetTitle}</b>. Keep this page open.
              </p>
            </>
          )}
        </div>
      ) : null}
{phase === "done" && savedUi ? (
        (() => {
          // The item's lead picture, else the first shot just taken.
          const onPic = onFileCand ? itemPictures(onFileCand)[0] : null;
          const backHref = isPreset ? preset.returnUrl || "" : doneReturnUrl(null, quick);
          const d = doneScreen({
            count: doneSnap?.n || 0,
            stored: doneSnap?.stored ?? null,
            picture: onPic?.url || doneSnap?.first?.previewUrl || doneSnap?.first?.url || "",
            name: onFileCand ? itemName(onFileCand) : "",
            barcode: isPreset ? preset.barcode || "" : ident?.noBarcode ? "" : ident?.barcode || barcode || "",
            autoReturn,
            returnUrl: backHref,
            tap: isIOS(),
            desk: Boolean(quick?.desk),
          });
          return (
            <div className="cap-saved" role="status">
              <div className={"cap-saved-pic" + (onPic && onPic.kind !== "photo" ? " cap-saved-pic--pack" : "")}>{d.picture ? <img src={d.picture} alt="" /> : null}</div>
              <span className={"cap-saved-pill" + (d.desk ? " cap-saved-pill--desk" : "")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
                {d.pill}
              </span>
              <span className="cap-saved-name">{d.title}</span>
              {d.line ? (
                <span className="cap-saved-line">
                  {d.spinner ? <i className="cap-saved-spin" /> : null}
                  {d.desk ? LAPTOP : null}
                  {d.line}
                </span>
              ) : null}
              {d.desk ? null : <div className="cap-saved-foot">
                {/* STILL A REAL LINK, even after the automatic return: that one
                    runs only inside the Done tap's gesture window, and this is the
                    way back when the commit took longer or the tab is reopened. */}
                {d.primary.href ? (
                  <a href={d.primary.href} className="cap-saved-go">{d.primary.label}</a>
                ) : onBack ? (
                  <button type="button" className="cap-saved-go" onClick={onBack}>{d.primary.label}</button>
                ) : null}
                <button type="button" className="cap-saved-more" onClick={takeMore}>Take more photos</button>
              </div>}
            </div>
          );
        })()
      ) : null}
{phase === "gone" ? (
        <div className="cap-panel" role="alert">
          <h2>This product is gone</h2>
          <p>It was deleted in Shopify. Go back to Shopify.</p>
        </div>
      ) : null}
    </>
  );
}
